import { useState, useCallback, useEffect } from 'react';
import styled from '@emotion/styled';
import type { QuickChartData } from '@trinity/chat';
import { Remirror as RemirrorTypes, setInsightsDataUtil } from '@trinity/chat';
import type { RemirrorEventListener } from 'remirror';
import { ColorSelect, defaultColor } from '../ColorSelect';
import Input from '../Input';
import { Header, Modal } from '../Modal';
import ButtonSet from '../Modal/ButtonSet';
import Button from '../presentation/Button';
import type { BasicMarkupEditorExtensions } from '../Remirror/BasicMarkupEditor';
import { BasicMarkupEditor } from '../Remirror/BasicMarkupEditor';

type DataTableSettingsProps = {
  open: boolean;
  onClose: () => void;
  chartData: QuickChartData & { type: 'data-table' };
  onSave: (updatedChartData: QuickChartData & { type: 'data-table' }) => void;
};
type RootNode = RemirrorTypes.RootNode;
const NodeType = RemirrorTypes.NodeType;

export function DataTableSettings({ open, onClose, chartData, onSave }: DataTableSettingsProps) {
  const [title, setTitle] = useState('');
  const [headerColor, setHeaderColor] = useState(chartData.headerColor || defaultColor);
  const [columnNames, setColumnNames] = useState<string[][]>(
    chartData.customColumnNames || chartData.data.columnNames,
  );
  const [rowNames, setRowNames] = useState<string[][]>(
    chartData.customRowNames || chartData.data.rowNames,
  );

  const [insightsContent, setInsightsContent] = useState<RootNode | null>(() => {
    return setInsightsDataUtil(chartData);
  });

  const [touched, setTouched] = useState(false);

  const handleInsightsChange: RemirrorEventListener<BasicMarkupEditorExtensions> =
    useCallback((params) => {
      if (!params.tr?.docChanged) return;

      const newValue = params.state.doc.toJSON() as RootNode;
      setInsightsContent(newValue);
      setTouched(true);
    }, []);

  useEffect(() => {
    if (open) {
      if (chartData.title.length > 60) {
        setTitle(chartData.title.slice(0, 60));
      } else {
        setTitle(chartData.title);
      }
    }
  }, [open, chartData]);

  const handleTitleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
    setTouched(true);
  }, []);

  const handleColorChange = useCallback((color: string) => {
    setHeaderColor(color);
    setTouched(true);
  }, []);

  const handleColumnNameChange = useCallback((rowIndex: number, colIndex: number, value: string) => {
    setColumnNames(prev => {
      const updated = [...prev];
      if (!updated[rowIndex]) updated[rowIndex] = [];
      updated[rowIndex][colIndex] = value;
      return updated;
    });
    setTouched(true);
  }, []);

  const handleRowNameChange = useCallback((rowIndex: number, colIndex: number, value: string) => {
    setRowNames(prev => {
      const updated = [...prev];
      if (!updated[rowIndex]) updated[rowIndex] = [];
      updated[rowIndex][colIndex] = value;
      return updated;
    });
    setTouched(true);
  }, []);

  const handleSave = useCallback(() => {

    let insightsString = '';
    if (
      insightsContent &&
      insightsContent.content &&
      insightsContent.content.length > 0
    ) {
      // Only serialize if there's actual content (not just empty paragraphs)
      const hasContent = insightsContent.content.some(
        (node) => node.content && node.content.length > 0,
      );
      if (hasContent) {
        insightsString = JSON.stringify(insightsContent);
      }
    }

    const updatedChartData: QuickChartData & { type: 'data-table' } = {
      ...chartData,
      title: title.trim(),
      headerColor,
      customColumnNames: columnNames,
      customRowNames: rowNames,
      insights: insightsString || undefined,
    };
    onSave(updatedChartData);
    onClose();
  }, [chartData, title, headerColor, columnNames, rowNames, insightsContent, onSave, onClose]);

  const handleCancel = useCallback(() => {
    // Reset to original values
    setTitle(chartData.title || '');
    setHeaderColor(chartData.headerColor || defaultColor);
    setColumnNames(chartData.customColumnNames || chartData.data.columnNames);
    setRowNames(chartData.customRowNames || chartData.data.rowNames);
    setTouched(false);
    onClose();
  }, [chartData, onClose]);

  // Validation
  const trimmedTitle = title.trim();
  const titleLength = trimmedTitle.length;
  const minLen = 1;
  const maxLen = 60;
  const titleValid = titleLength >= minLen && titleLength <= maxLen;
  const canSave = titleValid && trimmedTitle.length > 0;
  const showTitleError = touched && !titleValid;
  const titleErrorMessage = showTitleError
    ? (titleLength < minLen ? `Title is required.` : `Title must be less than or equal to ${maxLen} characters.`)
    : '';

  return (
    <Modal open={open} onClose={handleCancel}>
      <Header>Data Table Settings</Header>

      <ModalSection>
        <ModalLabel>Table Title *</ModalLabel>
        <Input
          name="table-title"
          type="text"
          value={title}
          onChange={handleTitleChange}
          style={{ width: '100%' }}
          placeholder="Enter table title" />
        <FieldMeta>
          <FieldInfo>{titleLength}/{maxLen} characters</FieldInfo>
          {titleErrorMessage && <FieldError>{titleErrorMessage}</FieldError>}
        </FieldMeta>
      </ModalSection>

      <ModalSection>
        <ModalLabel>Color</ModalLabel>
        <ColorSelect
          color={headerColor}
          onChange={handleColorChange} />
      </ModalSection>

      <ModalSection>
        <ModalLabel>Row Labels ({rowNames.length} rows)</ModalLabel>
        <LabelsList>
          {rowNames.map((rowGroup, rowIndex) => (
            <HierarchyRow key={rowIndex}>
              <RowLabel>Row {rowIndex + 1}:</RowLabel>
              <HierarchyInputs>
                {rowGroup.map((label, colIndex) => (
                  <HierarchyInput
                    key={`${rowIndex}-${colIndex}`}
                    type="text"
                    value={label || ''}
                    onChange={e => handleRowNameChange(rowIndex, colIndex, e.target.value)}
                    placeholder={`Level ${colIndex + 1}`}
                    maxLength={60}
                    $level={colIndex} />
                ))}
              </HierarchyInputs>
            </HierarchyRow>
          ))}
        </LabelsList>
      </ModalSection>

      <ModalSection>
        <ModalLabel>Column Labels ({columnNames.length} columns)</ModalLabel>
        <LabelsList>
          {columnNames.map((columnGroup, rowIndex) => (
            <HierarchyRow key={rowIndex}>
              <RowLabel>Column {rowIndex + 1}:</RowLabel>
              <HierarchyInputs>
                {columnGroup.map((label, colIndex) => (
                  <HierarchyInput
                    key={`${rowIndex}-${colIndex}`}
                    type="text"
                    value={label || ''}
                    onChange={e => handleColumnNameChange(rowIndex, colIndex, e.target.value)}
                    placeholder={`Level ${colIndex + 1}`}
                    maxLength={60}
                    $level={colIndex} />
                ))}
              </HierarchyInputs>
            </HierarchyRow>
          ))}
        </LabelsList>
      </ModalSection>

      <ModalSection>
        <ModalLabel>Add Insights (Optional)</ModalLabel>
        <InsightsEditor>
          <BasicMarkupEditor
            content={insightsContent}
            onChange={handleInsightsChange}
          />
        </InsightsEditor>
        <FieldHint>
          Add context, analysis, or additional information about this bar chart.
        </FieldHint>
      </ModalSection>

      <ButtonSet>
        <Button.Destructive
          variant="brick"
          onClick={handleCancel}>
          Cancel
        </Button.Destructive>
        <Button.Primary
          variant="brick"
          onClick={handleSave}
          disabled={!canSave}>
          Save
        </Button.Primary>
      </ButtonSet>
    </Modal>
  );
}

const ModalSection = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  marginBottom: '20px',
});

const ModalLabel = styled.div({
  fontSize: 18,
  fontFamily: 'var(--font-semibold)',
  color: 'var(--black)',
});

const LabelsList = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  height: '200px',
  overflowY: 'auto',
  border: '1px solid var(--gray-l)',
  borderRadius: '4px',
  padding: '12px',
});

const HierarchyRow = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  padding: '8px',
  backgroundColor: 'var(--gray-xxl)',
  borderRadius: '4px',
  border: '1px solid var(--gray-xl)',
});

const RowLabel = styled.div({
  fontSize: '12px',
  fontWeight: 600,
  color: 'var(--gray-d)',
  marginBottom: '4px',
});

const HierarchyInputs = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
});

const HierarchyInput = styled(Input)<{ $level: number }>(({ $level }) => ({
  paddingLeft: `${8 + ($level * 16)}px`,
  fontSize: '14px',
  backgroundColor: $level > 0 ? 'var(--gray-ul)' : 'white',
  boxSizing: 'border-box',
}));

const FieldMeta = styled.div({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
  marginTop: '4px',
});

const FieldInfo = styled.span({
  fontSize: 11,
  color: 'var(--gray-d)',
});

const FieldError = styled.span({
  fontSize: 11,
  color: '#d32f2f',
  fontWeight: 600,
});

const FieldHint = styled.div({
  fontSize: '12px',
  color: '#666',
  marginTop: '8px',
  fontStyle: 'italic',
});

const InsightsEditor = styled.div({
  minHeight: '120px',
  marginTop: '8px',
});